/**
 * Admin Model-Capability Dashboard
 *
 * Read view of the dynamic LLM capability registry per (generation_type,
 * model): static floor, live verdict, schema-failure telemetry, and a manual
 * override (pin capable / incapable / clear) that the verdict respects above
 * the dynamic data.
 *
 * Mirrors AdminMetricsPage (admin-gated, cloud API). Internal admin tooling —
 * copy is intentionally literal English (no i18n keys added).
 */

import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import {
  fetchModelCapability,
  setCapabilityOverride,
  type CapabilityOverride,
  type CapabilityRow,
  type ModelCapabilityReport,
} from '../../services/modelCapabilityAPI';
import { extractErrorMessage } from '../../utils/httpUtils';

function verdictBadge(row: CapabilityRow): JSX.Element {
  const capable = row.is_capable;
  const cls = capable ? 'bg-green-900/40 text-green-300' : 'bg-red-900/40 text-red-300';
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${cls}`}>
      {capable ? 'capable' : 'demoted'}
    </span>
  );
}

export function ModelCapabilityPage(): JSX.Element {
  const { user, claims, getIdToken } = useAuth();
  const isAdmin = claims?.admin === true;

  const [report, setReport] = useState<ModelCapabilityReport | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!user || !isAdmin) return;
    setLoading(true);
    setError(null);
    try {
      const token = await getIdToken();
      if (!token) throw new Error('Authentication required');
      setReport(await fetchModelCapability(token));
    } catch (e) {
      setError(extractErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [user, isAdmin, getIdToken]);

  useEffect(() => {
    void load();
  }, [load]);

  const applyOverride = useCallback(
    async (generationType: string, model: string, override: CapabilityOverride) => {
      const key = `${generationType}::${model}`;
      setBusyKey(key);
      setError(null);
      try {
        const token = await getIdToken();
        if (!token) throw new Error('Authentication required');
        const result = await setCapabilityOverride(token, generationType, model, override);
        if (!result.ok) throw new Error(result.error || 'Override failed');
        await load();
      } catch (e) {
        setError(extractErrorMessage(e));
      } finally {
        setBusyKey(null);
      }
    },
    [getIdToken, load]
  );

  if (!isAdmin) {
    return (
      <div className="p-6">
        <div className="card bg-black/30 p-6 text-white/80">Admin access required.</div>
      </div>
    );
  }

  return (
    <div className="px-3 sm:px-6 py-4 sm:py-6 space-y-4">
      <div>
        <h1 className="section-heading-xl">Model Capability Registry</h1>
        <p className="text-white/70 text-sm">
          Static floor ∪ production telemetry ∪ manual override. A statically-capable model is
          auto-demoted once it reaches {report?.min_samples ?? '—'} attempts with a schema-failure
          rate above {report ? `${Math.round(report.schema_failure_rate_threshold * 100)}%` : '—'}.
        </p>
        {report && !report.store_available && (
          <p className="text-amber-300 text-sm mt-1">
            Telemetry store unavailable on this server — verdicts reflect the static floor only.
          </p>
        )}
      </div>

      {error && <div className="card bg-red-900/30 text-red-200 p-3 text-sm">{error}</div>}

      <div className="flex items-center gap-3">
        <button
          type="button"
          className="btn-secondary min-h-[44px]"
          onClick={() => void load()}
          disabled={loading}
        >
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {report &&
        Object.entries(report.generation_types).map(([gtype, block]) => (
          <div key={gtype} className="card bg-black/30 p-3 sm:p-4">
            <div className="flex items-center gap-2 mb-2">
              <h2 className="text-lg font-semibold text-white">{gtype}</h2>
              <span
                className={`px-2 py-0.5 rounded text-xs ${
                  block.gated ? 'bg-purple-900/40 text-purple-200' : 'bg-white/10 text-white/60'
                }`}
              >
                {block.gated ? 'hard-gated' : 'observe-only'}
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-white/80">
                <thead className="text-white/50 text-xs">
                  <tr className="text-left">
                    <th className="py-1 pr-2">Model</th>
                    <th className="py-1 pr-2">Verdict</th>
                    <th className="py-1 pr-2">Floor</th>
                    <th className="py-1 pr-2">Attempts</th>
                    <th className="py-1 pr-2">Fail rate</th>
                    <th className="py-1 pr-2">Override</th>
                    <th className="py-1 pr-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {block.rows.map((row) => {
                    const key = `${gtype}::${row.model}`;
                    const busy = busyKey === key;
                    return (
                      <tr key={key} className="border-t border-white/10">
                        <td className="py-1.5 pr-2 font-mono text-xs">{row.model}</td>
                        <td className="py-1.5 pr-2">{verdictBadge(row)}</td>
                        <td className="py-1.5 pr-2">{row.statically_capable ? '✓' : '—'}</td>
                        <td className="py-1.5 pr-2">{row.attempts}</td>
                        <td className="py-1.5 pr-2">
                          {Math.round(row.schema_failure_rate * 100)}%
                        </td>
                        <td className="py-1.5 pr-2">{row.manual_override ?? '—'}</td>
                        <td className="py-1.5 pr-2">
                          <div className="flex flex-wrap gap-1">
                            <button
                              type="button"
                              disabled={busy}
                              className="px-2 py-1 rounded bg-green-900/40 text-green-200 text-xs disabled:opacity-50"
                              onClick={() => void applyOverride(gtype, row.model, 'force_capable')}
                            >
                              capable
                            </button>
                            <button
                              type="button"
                              disabled={busy}
                              className="px-2 py-1 rounded bg-red-900/40 text-red-200 text-xs disabled:opacity-50"
                              onClick={() =>
                                void applyOverride(gtype, row.model, 'force_incapable')
                              }
                            >
                              incapable
                            </button>
                            <button
                              type="button"
                              disabled={busy || row.manual_override === null}
                              className="px-2 py-1 rounded bg-white/10 text-white/70 text-xs disabled:opacity-50"
                              onClick={() => void applyOverride(gtype, row.model, null)}
                            >
                              clear
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ))}
    </div>
  );
}
